import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  throwError,
  timer,
} from 'rxjs';
import {
  catchError,
  delay,
  finalize,
  shareReplay,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { User as UserModel } from 'src/app/models/user.model';
import { AUTH_SESSION_KEY } from '../../constants/auth-key.constant';
import { AuthSession, User, UserCreatePayload } from './interfaces';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly API_URL = environment.API_url;
  readonly userSubject = new BehaviorSubject<User | null | any>(null);
  readonly user$ = this.userSubject.asObservable();
  refreshInProgress$: Observable<AuthSession> | null = null;
  signupId!: string;
  refreshTokenInProgress: boolean = false;
  refreshTokenSubject$: BehaviorSubject<AuthSession | null> =
    new BehaviorSubject<AuthSession | null>(null);

  private autoRefreshSubscription?: Subscription;
  private readonly REFRESH_THRESHOLD_SECONDS = 180; // 3 minutes

  constructor(
    private http: HttpClient,
    private router: Router,
    private dialog: MatDialog
  ) {
    this.autoLogin();
  }

  getCurrentUser(): User | null {
    return this.userSubject.getValue();
  }

  updateUser(user: User | UserModel | null): void {
    this.userSubject.next(user);
  }

  login(mail: string, password: string) {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: 'Basic ' + btoa(mail + ':' + password),
      skip: 'true'
    });

    return this.http
      .post<AuthSession>(this.API_URL + 'api/auth/login', null, {
        headers: headers
      })
      .pipe(
        catchError(this.handleError),
        tap((authSession) => {
          this.updateToken(authSession);
        })
      );
  }

  logout() {
    this.cancelAutoRefresh();
    sessionStorage.removeItem('newsBannerShown');
    this.dialog.closeAll();

    if (!this.userSubject.value) {
      this.logoutByInvalidToken();
    } else {
      let headers = new HttpHeaders({
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: 'Basic ' + this.userSubject?.value?.token
      });

      this.http
        .post(this.API_URL + 'api/auth/logout', null, {
          headers: headers
        })
        .pipe(take(1))
        .subscribe(
          (resp) => {
            this.router.navigate(['/login']);
            this.removeAuthSession();
            this.updateUser(null);
          },
          (err) => {
            this.logoutByInvalidToken();
          }
        );
    }
  }

  signup(user: UserCreatePayload) {
    let headers = new HttpHeaders({ skip: 'true' });
    const userData = {
      company: user.company,
      companyType: user.companyType,
      phone: user.phone,
      fullName: user.fullName
    };
    return this.http
      .post<string>(`${this.API_URL}api/auth/signup`, userData, {
        headers: headers,
      })
      .pipe(catchError(this.handleError));
  }

  completeSignup(user: UserCreatePayload) {
    let headers = new HttpHeaders({ skip: 'true' });

    return this.http
      .put(
        this.API_URL + `api/auth/finish/${this.signupId}`,
        {
          mail: user.mail,
          password: user.password
        },
        { headers: headers }
      )
      .pipe(catchError(this.handleError));
  }

  autoLogin() {
    const authSession = this.getAuthSession();
    const url = this.router.routerState.snapshot.url;

    if (authSession) {
      this.setUser(authSession);
      this.scheduleTokenRefresh();

      if (url === '/login' || url === '/cadastro') {
        this.router.navigate(['/dashboard']);
      }
    }
  }

  resetPassword(mail: string) {
    let headers = new HttpHeaders({ skip: 'true', ['skip-error-handling']: 'true' });

    return this.http
      .post(this.API_URL + 'api/auth/reset/password', mail, {
        headers: headers,
      })
      .pipe(take(1));
  }

  createdPassword(id: string, password: string) {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: 'Basic ' + btoa(password),
      skip: 'true',
    });

    return this.http.post(this.API_URL + 'api/auth/recover/' + id, null, {
      headers: headers,
    });
  }

  passwordUpdate(id: string, oldPassword: string, newPassword: string) {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      pwd: 'Basic ' + btoa(oldPassword + ':' + newPassword),
    });

    return this.http.put(
      this.API_URL + 'secure-api/user/passwordUpdate/' + id,
      null,
      { headers: headers }
    );
  }

  logoutByInvalidToken() {
    this.refreshTokenInProgress = false;
    this.refreshInProgress$ = null;
    this.refreshTokenSubject$.next(null);
    this.cancelAutoRefresh();
    sessionStorage.removeItem('newsBannerShown');
    this.dialog.closeAll();
    this.updateUser(null);
    this.router.navigate(['/login']);
    this.removeAuthSession();
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('userData');
  }

  updateStatus(id: string, status: any) {
    return this.http.put(
      this.API_URL + 'secure-api/tenants/status/' + id,
      status
    );
  }

  public getAuthSession(): AuthSession | null {
    const authSessionStr = localStorage.getItem(AUTH_SESSION_KEY) ?? null;
    const authSession = authSessionStr
      ? (JSON.parse(authSessionStr) as AuthSession)
      : null;

    return authSession;
  }

  public isTokenInvalid() {
    const accessToken = this.getAuthSession()?.token;

    if (!accessToken) return true;

    const exp = this.getUserByJWT(accessToken)?.exp;
    const now = Math.floor(Date.now() / 1000);

    return exp ? now >= exp : true;
  }

  public updateToken(newAuthSession: AuthSession) {
    this.storageAuthSession(newAuthSession);
    this.setUser(newAuthSession);
    this.scheduleTokenRefresh();
  }

  private scheduleTokenRefresh(): void {
    this.cancelAutoRefresh();

    const authSession = this.getAuthSession();
    if (!authSession?.token) return;

    const user = this.getUserByJWT(authSession.token);
    if (!user?.exp) return;

    const expiresAt = user.exp * 1000;
    const now = Date.now();
    const timeUntilExpiry = expiresAt - now;

    const refreshTime = timeUntilExpiry - this.REFRESH_THRESHOLD_SECONDS * 1000;

    if (refreshTime <= 0) {
      console.warn('Token próximo da expiração, realizando refresh imediato');
      this.newRefreshToken()
        .pipe(take(1))
        .subscribe({
          error: (err) => console.error('Erro no refresh automático:', err),
        });
      return;
    }

    this.autoRefreshSubscription = timer(refreshTime)
      .pipe(
        switchMap(() => {
          return this.newRefreshToken();
        }),
        take(1)
      )
      .subscribe();
  }

  private cancelAutoRefresh(): void {
    if (this.autoRefreshSubscription) {
      this.autoRefreshSubscription.unsubscribe();
      this.autoRefreshSubscription = undefined;
    }
  }

  private storageAuthSession(authSession: AuthSession) {
    localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(authSession));
  }

  private removeAuthSession() {
    localStorage.removeItem(AUTH_SESSION_KEY);
  }

  private setUser(authSession: AuthSession) {
    const _user = this.newUser(
      this.getUserByJWT(authSession.token),
      authSession.token
    );
    this.updateUser(_user);
  }

  private getUserByJWT(tokenJWT: string | null): User | null {
    if (tokenJWT == null) return null;

    const base64Url = tokenJWT.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    return JSON.parse(jsonPayload);
  }

  private newUser(user: any, token: string) {
    const _user = new UserModel(
      user.fullName,
      user.phone,
      user.mail,
      user.id,
      token,
      user.userType,
      user.enable,
      user.lastLogin,
      (user.tenant = {
        companyId: user.tenant.companyId,
        companyType: user.tenant.companyType,
        id: user.tenant.id,
        companyName: user.tenant.companyName,
        companyCnpj: user.tenant.companyCnpj,
        companyCompletionPercentage: user.tenant.companyCompletionPercentage,
        tenantStatus: user.tenant.tenantStatus,
        sellCcb: user.tenant.sellCcb,
        ccbBuyer: user.tenant.ccbBuyer,
        enabled: user.tenant.enabled,
        isCanViewFiduciaSimulation: user.tenant.isCanViewFiduciaSimulation,
        isCcbConsumer: user.tenant.isCcbConsumer,
        isCcbProvider: user.tenant.isCcbProvider,
        companyNameProvider: user.tenant.companyNameProvider,
        companyIdProvider: user.tenant.companyIdProvider,
      })
    );

    return _user;
  }

  private handleError(errorRes: HttpErrorResponse) {
    let errorMessage;

    if (!errorRes.error || !errorRes.error.error) {
      return throwError(errorMessage);
    }

    const getError = this.errors[errorRes.error.message];

    if (getError) {
      errorMessage = this.errors[errorRes.error.message]();
    }

    return throwError(errorMessage);
  }

  getStatus() {
    return this.http.get(environment.API_url + 'secure-api/tenants/status');
  }

  getSignupId() {
    return this.signupId;
  }

  setSignupId(signupId: string) {
    this.signupId = signupId;
  }

  newRefreshToken(): Observable<AuthSession> {
    if (this.refreshInProgress$) {
      return this.refreshInProgress$;
    }

    const refreshToken = this.getAuthSession()?.refreshToken;

    if (!refreshToken) {
      console.error('Refresh token não encontrado');
      this.logoutByInvalidToken();
      return throwError(new Error('Refresh token não encontrado.'));
    }

    this.refreshTokenInProgress = true;
    this.refreshTokenSubject$.next(null);

    this.refreshInProgress$ = this.http
      .post<AuthSession>(
        `${this.API_URL}api/auth/open/refresh`,
        {},
        {
          headers: new HttpHeaders({
            skip: 'true',
            RefreshToken: refreshToken,
          }),
        }
      )
      .pipe(
        shareReplay(1),
        tap((newSession) => {
          this.updateToken(newSession);
          this.refreshTokenSubject$.next(newSession);
        }),
        delay(100),
        finalize(() => {
          this.refreshTokenInProgress = false;
          this.refreshInProgress$ = null;
        }),
        catchError((error) => {
          console.error('Erro ao atualizar o token:', error || error.message);
          this.logoutByInvalidToken();
          return throwError(error);
        })
      );

    return this.refreshInProgress$;
  }

  errors: any = {
    CPF_ALREADY_REGISTERED: () => {
      return 'Já existe um cadastro realizado com este CPF';
    },
    MAIL_ALREADY_REGISTERED: () => {
      return 'Já existe um cadastro realizado com este e-mail';
    },
    CNPJ_ALREADY_REGISTERED: () => {
      return 'Já existe um cadastro realizado com este CNPJ';
    },
    MAIL_DO_NOT_EXISTS: () => {
      return 'O e-mail informado não está cadastrado.';
    },
    INVALID_PASSWORD: () => {
      return 'O e-mail informado não está cadastrado ou a senha está incorreta. Verifique os dados digitados e tente entrar novamente';
    },
    INVALID_CNPJ: () => {
      return 'O CNPJ informado é inválido.';
    },
    PHONE_ALREADY_REGISTERED: () => {
      return 'Já existe um cadastro realizado com este telefone';
    },
  };
}
